import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isToday, isTomorrow, isThisWeek } from 'date-fns';
import * as Icon from 'react-feather';
import { AccountIcons } from '../../components/general/AccountIcons';
import myImage from '../../assets/empty-box-2.png';

const PER_PAGE = 50;

function formatTime(gmtDate) {
  if (!gmtDate || typeof gmtDate !== 'string') return '—';
  const parts = gmtDate.split(/[- :]/);
  const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], parts[3] || 0, parts[4] || 0, parts[5] || 0));
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
}

function parseDateUTC(gmtDate) {
  if (!gmtDate || typeof gmtDate !== 'string') return new Date(0);
  const parts = gmtDate.split(/[- :]/);
  return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
}

function getDateLabel(date) {
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  if (isThisWeek(date, { weekStartsOn: 1 })) return format(date, 'EEEE');
  return format(date, 'MMMM d');
}

function ListView() {
  const navigate      = useNavigate();
  const [posts, setPosts]       = useState([]);
  const [page, setPage]         = useState(1);
  const [hasMore, setHasMore]   = useState(true);
  const [loading, setLoading]   = useState(false);
  const [initialDone, setInitialDone] = useState(false);

  // Use a ref-based lock to prevent concurrent fetches
  const fetchingRef = useRef(false);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  const fetchPage = useCallback(async (pg) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);

    try {
      const params = new URLSearchParams({ page: pg, per_page: PER_PAGE });
      const res  = await fetch(
        `https://api.schedpilot.com/wp-json/api/v1/publish-list?${params}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      const newPosts    = data.posts       ?? [];
      const totalPages  = data.total_pages ?? 1;

      setPosts(prev => pg === 1 ? newPosts : [...prev, ...newPosts]);
      setHasMore(pg < totalPages);
    } catch (e) {
      console.error('[ListView] fetch error:', e);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setInitialDone(true);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  // Load next page whenever `page` increments beyond 1
  useEffect(() => {
    if (page === 1) return;
    fetchPage(page);
  }, [page, fetchPage]);

  // IntersectionObserver — fires when sentinel enters viewport
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !fetchingRef.current) {
          setPage(prev => prev + 1);
        }
      },
      { rootMargin: '200px' }   // start loading 200px before the sentinel
    );

    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);

    return () => observerRef.current?.disconnect();
  }, [hasMore]);

  // Group accumulated posts by date (already returned ASC from API)
  const grouped = useMemo(() => {
    const map = {};
    posts.forEach(post => {
      const key = parseDateUTC(post.scheduled_date).toDateString();
      if (!map[key]) map[key] = { date: parseDateUTC(post.scheduled_date), posts: [] };
      map[key].posts.push(post);
    });
    return Object.values(map);
  }, [posts]);

  // Initial skeleton while first page loads
  if (!initialDone) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{
            height: 62, borderRadius: 10, background: '#f1f5f9',
            border: '1px solid #e8edf2', borderLeft: '3px solid #bbf7d0',
            animation: 'pulse 1.4s ease-in-out infinite',
          }} />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 12, border: '1px solid #e8edf2' }}>
        <img src={myImage} alt="" style={{ width: 80, opacity: 0.45, marginBottom: 16 }} />
        <p style={{ fontWeight: 600, color: '#475569', marginBottom: 4 }}>No posts yet</p>
        <p style={{ fontSize: '0.875rem', color: '#94a3b8', margin: 0 }}>Your publish history will appear here.</p>
      </div>
    );
  }

  return (
    <div>
      {grouped.map(({ date, posts: dayPosts }) => {
        const isCurrentDay = isToday(date);

        return (
          <div key={date.toDateString()} style={{ marginBottom: 28 }}>
            {/* Date header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ fontWeight: 700, fontSize: '0.85rem', color: isCurrentDay ? '#4f46e5' : '#0f172a', flexShrink: 0 }}>
                {getDateLabel(date)}
              </span>
              <span style={{ fontSize: '0.78rem', color: '#94a3b8', flexShrink: 0 }}>
                {format(date, 'MMM d')}
              </span>
              <div style={{ flex: 1, height: 1, background: '#e8edf2' }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8', flexShrink: 0 }}>
                {dayPosts.length} post{dayPosts.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Post cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dayPosts.map(post => (
                <PostCard key={post.id} post={post} onNavigate={navigate} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Scroll sentinel — IntersectionObserver watches this */}
      <div ref={sentinelRef} style={{ height: 1 }} />

      {/* Loading more indicator */}
      {loading && initialDone && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Icon.Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
          Loading more…
        </div>
      )}

      {/* End of list */}
      {!hasMore && posts.length > 0 && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#cbd5e1', fontSize: '0.8rem' }}>
          All posts loaded
        </div>
      )}
    </div>
  );
}

const STATUS_COLOR = {
  posted:           '#16a34a',
  failed:           '#ef4444',
  processing:       '#f59e0b',
  scheduled:        '#4f46e5',
  pending_approval: '#7c3aed',
};

function PostCard({ post, onNavigate }) {
  const hasImage   = post.images?.length > 0;
  const hasVideo   = post.videos?.length > 0;
  const accent     = STATUS_COLOR[post.status] || STATUS_COLOR.scheduled;

  return (
    <div
      onClick={() => onNavigate(`/single-post/${post.id}`)}
      style={{
        background: '#fff',
        border: '1px solid #e8edf2',
        borderLeft: `3px solid ${accent}`,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 2px 14px rgba(0,0,0,0.07)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Time */}
      <div style={{
        padding: '14px 16px',
        borderRight: '1px solid #f1f5f9',
        flexShrink: 0,
        minWidth: 78,
        textAlign: 'center',
      }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>
          {formatTime(post.scheduled_date)}
        </span>
      </div>

      {/* Accounts */}
      <div style={{
        padding: '14px 12px',
        borderRight: '1px solid #f1f5f9',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
      }}>
        <AccountIcons accounts={post.accounts || []} />
      </div>

      {/* Content */}
      <div style={{ padding: '14px', flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0,
          fontSize: '0.875rem',
          color: '#334155',
          lineHeight: 1.55,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}>
          {post.content || post.videoTitle || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No text</span>}
        </p>
      </div>

      {/* Thumbnail */}
      {(hasImage || hasVideo) && (
        <div style={{ width: 58, height: 58, flexShrink: 0, marginRight: 12, borderRadius: 8, overflow: 'hidden', border: '1px solid #e8edf2' }}>
          {hasVideo ? (
            <div style={{ width: '100%', height: '100%', background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon.Play size={18} color="#fff" fill="#fff" />
            </div>
          ) : (
            <img src={post.images[0].url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
        </div>
      )}

      {/* Actions */}
      <div
        style={{ padding: '14px 12px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={() => onNavigate(`/single-post/${post.id}`)}
          style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7,
            padding: '5px 12px', cursor: 'pointer', color: '#64748b',
            fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 5,
            fontWeight: 500,
          }}
        >
          <Icon.Edit2 size={12} /> Edit
        </button>
      </div>
    </div>
  );
}

export default ListView;
